import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { Controller } from 'react-hook-form';
import "react-datepicker/dist/react-datepicker.css";

export function DateRangePicker({
    control,
    setValue,
    initialStartDate,
    initialEndDate,
    startDateName = "data.fromDate", // Default names matching common structure
    endDateName = "data.toDate",
    errors,
    minDate = new Date() // Default minimum date to today
}) {
    const [startDate, setStartDate] = useState(initialStartDate ? new Date(initialStartDate) : null);
    const [endDate, setEndDate] = useState(initialEndDate ? new Date(initialEndDate) : null);

    // Effect to sync initial props to state if they change
    useEffect(() => {
        setStartDate(initialStartDate ? new Date(initialStartDate) : null);
    }, [initialStartDate]);

    useEffect(() => {
        setEndDate(initialEndDate ? new Date(initialEndDate) : null);
    }, [initialEndDate]);

    const handleFromDateChange = (date) => {
        setStartDate(date);
        setValue(startDateName, date, { shouldValidate: true }); // Trigger validation on change

        // Auto-adjust end date if it's invalid relative to the new start date
        if (!endDate || date >= endDate) {
            const newEndDate = new Date(date);
            newEndDate.setHours(newEndDate.getHours() + 1); // Default to 1 hour later
            setEndDate(newEndDate);
            setValue(endDateName, newEndDate, { shouldValidate: true });
        }
    };

    const handleToDateChange = (date) => {
        setEndDate(date);
        setValue(endDateName, date, { shouldValidate: true }); // Trigger validation on change
    };

    // Helper to get nested error messages
    const getError = (name) => {
        const nameParts = name.split('.');
        let error = errors;
        for (const part of nameParts) {
            if (!error) break;
            error = error[part];
        }
        return error;
    };
    
    const startDateError = getError(startDateName);
    const endDateError = getError(endDateName);


    return (
        <div className="row">
            <div className="col">
                <Controller
                    name={startDateName}
                    control={control}
                    // Rules can be added here if not using schema validation fully
                    render={({ field }) => ( // Use field from render prop if needed, though we use state here
                        <div className="form-group mb-2">
                            <label className="mb-0">From date</label>
                            <DatePicker
                                selected={startDate}
                                onChange={handleFromDateChange}
                                className={`form-control ${startDateError ? 'is-invalid' : ''}`}
                                minDate={minDate}
                                showTimeSelect
                                timeFormat="p"
                                timeIntervals={15}
                                timeCaption="time"
                                dateFormat="MMMM d, yyyy h:mm aa"
                                placeholderText="Select start date"
                            />
                            {startDateError && (
                                <div className="invalid-feedback mt-1">{startDateError.message}</div>
                            )}
                        </div>
                    )}
                />
            </div>
            <div className="col">
                <Controller
                    name={endDateName}
                    control={control}
                    // Rules can be added here if not using schema validation fully
                    render={({ field }) => ( // Use field from render prop if needed
                        <div className="form-group mb-2">
                            <label className="mb-0">To date</label>
                            <DatePicker
                                selected={endDate}
                                onChange={handleToDateChange}
                                className={`form-control ${endDateError ? 'is-invalid' : ''}`}
                                minDate={startDate || minDate} // Min end date is start date or the overall minDate
                                showTimeSelect
                                timeFormat="p"
                                timeIntervals={15}
                                timeCaption="time"
                                dateFormat="MMMM d, yyyy h:mm aa"
                                placeholderText="Select end date"
                            />
                           {endDateError && (
                                <div className="invalid-feedback mt-1">{endDateError.message}</div>
                            )}
                        </div>
                    )}
                />
            </div>
        </div>
    );
} 